/* Copyright (c) 2023 MOCA Systems, Inc. All rights reserved. */
import { defer, from, iif, Observable, of } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';
import { inject } from '@angular/core';

import { Tenant } from '@touchplan/common';
import { tenantGetterService } from '@touchplan/firebase-common';
import { DEFAULT_TENANT_ID } from '@touchplan/fancy-referencer';

import { AccountCreationStore } from './account-creation-store.service';
import { AuthService, IEmailTenantExclusiveWithLocalData } from 'src/app/services/auth.service';
import { StringUtils } from 'src/app/utils/string-utils';
import { fancyDatabase } from '@touchplan/client-firebase';

export class AccountClaimsValidationService {
	private authService = inject(AuthService);
	private accountCreationStore = inject(AccountCreationStore);

	public validateAccountClaimsFromEmail$(email: string): Observable<Tenant> {
		return this.authService.queryTenantViaEmail(email).pipe(
			switchMap((tenantInfo) => {
				if (tenantInfo) {
					//load 'er into the store, and thus into auth service

					// If migration has pending state and claimed users then use pre-migration tenant
					if (tenantInfo.tenant && tenantInfo.pending && tenantInfo.claimedUsers) {
						return this.getPreMigrationTenant(tenantInfo).pipe(
							tap((tenant) => {
								if (tenant && !tenant.isDefaultTenant) {
									this.accountCreationStore.setTenant(tenant);
								}
							})
						);
					}

					// If migration is not pending then use post-migration tenant
					// or also if no claimed users use post-migration tenant because there is not pre-migration tenant.
					if (
						(tenantInfo.tenant && !tenantInfo.pending) ||
						(tenantInfo.tenant && tenantInfo.pending && !tenantInfo.claimedUsers)
					) {
						this.accountCreationStore.setTenant(tenantInfo.tenant);
					}

					return of(tenantInfo.tenant);
				} else {
					// return this.accountCreationStore.tenant$;
					//if not, check the domain
					return this.authService.queryTenantViaDomainKey(StringUtils.getDomainKeyFromEmail(email)).pipe(
						tap((tenant) => {
							if (tenant && tenant.loaded && tenant.authTenantId !== DEFAULT_TENANT_ID) {
								this.accountCreationStore.setTenant(tenant);
							}
						}),
						switchMap((tenant) =>
							iif(
								() => tenant && tenant.loaded && tenant.authTenantId !== DEFAULT_TENANT_ID,
								of(tenant),
								//if all else fails, take whatever's in the store already (probably from the url)
								defer(() => this.accountCreationStore.tenant$.pipe(take(1)))
							)
						)
					);
				}
			})
		);
	}

	private getPreMigrationTenant(tenantInfo: IEmailTenantExclusiveWithLocalData): Observable<Tenant> {
		const claimedUserTenants = Object.keys(tenantInfo.claimedUsers);
		if (!tenantInfo.tenant || claimedUserTenants.length === 0) {
			return of(undefined);
		}
		// It is assumed the claim users only include one Tenant.
		if (claimedUserTenants.length > 1) {
			console.log('Claim users should include at most one tenant. Tenant to use: ', claimedUserTenants[0]);
		}
		return from(tenantGetterService.getTenantViaAuthId(fancyDatabase, claimedUserTenants[0]));
	}
}
